import React, { useCallback } from "react";
import ReactDOM from "react-dom";

import {
	faBolt,
	faCheck,
	faEllipsisH,
	faLayerGroup,
	faPlus,
	faSlash,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { EventTarget } from "event-target-shim";
import fastdom from "fastdom";
import { compact, flatMap, flatten, groupBy, mapValues, memoize } from "lodash";
import { undo } from "prosemirror-history";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, NodeSelection, Plugin, TextSelection, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import useResizeObserver from "use-resize-observer";
import uuid4 from "uuid/v4";

import ViewportContext from "~/common/ViewportContext";
import { fetchFeature } from "~/lib/flippy";
import { CollabPluginKey } from "~/lib/prose/plugins/collab";
import { getContext } from "~/lib/prose/plugins/context";
import { choiceToTypeaheadResult, getMenuChoices, type SlashCommandChoice } from "~/lib/prose/plugins/slash";
import type { TypeAheadResult } from "~/lib/prose/plugins/typeahead";
import { markActive, nodeActive } from "~/lib/prose/utils";
import { useRecordingState } from "~/lib/videoRecording";
import { Tooltip } from "~/ui";
import { IconButton as _IconButton, ButtonLink } from "~/ui/legacyFellowIndex";
import { Menu } from "~/ui/menu";

import {
	MarkExtension,
	type MenuEntry,
	type MenuEntryComponentProps,
	MenuExtension,
	NodeExtension,
} from "../extension";
import { openNoteHistoryModal } from "../history";
import { MobileToolbar } from "./toolbarMobile";

import styles from "~/lib/prose/css/toolbar.module.css";

export type ToolbarItemProps = Omit<MenuEntryComponentProps, "children">;

export function ToolbarItem(props: ToolbarItemProps) {
	const { asMenuItem, entry, isMobile, editorView, active, disabled } = props;
	const onMenuItemClick = React.useCallback(
		ev => {
			editorView.focus();
			entry.run()(
				editorView.state,
				tr => {
					editorView.dispatch(tr);
					return true;
				},
				editorView,
				ev,
			);
			ev?.preventDefault();
		},
		[entry, editorView],
	);

	if (entry.mobileOnly && isMobile) return null;

	const tooltipText = isMobile || asMenuItem ? null : !!entry.keyboardShortcut ? entry.keyboardShortcut : entry.title;

	const menuEntryToShortcut: { [key: string]: string } = {
		"Talking point": "()",
		"Action item": "[]",
		"Heading 1": "#",
		"Heading 2": "##",
	};

	const toolbarItem = asMenuItem ? (
		<Menu.Item className={cx({ [styles.active]: active })} icon={entry.icon ?? faPlus} onSelect={onMenuItemClick}>
			{entry.title}
			{menuEntryToShortcut[entry.title] && (
				<span className={styles.menuItemShortcut}>{menuEntryToShortcut[entry.title]}</span>
			)}
		</Menu.Item>
	) : (
		<Tooltip key={entry.title} content={tooltipText} delay={500} placement="top">
			<button
				type="button"
				onClick={onMenuItemClick}
				className={cx(styles.item, { [styles.active]: active, [styles.disabled]: disabled })}
				disabled={disabled}
			>
				<FontAwesomeIcon icon={entry.icon} fixedWidth className={styles.icon} />
			</button>
		</Tooltip>
	);

	if (entry.component) {
		const Component = entry.component;
		return (
			<Component key={entry.title} {...props}>
				{toolbarItem}
			</Component>
		);
	}

	return toolbarItem;
}

export function isActive(entry: MenuEntry, extension: Toolbar, editorState: EditorState) {
	if (entry.active) return entry.active(editorState);
	const ext = extension.entryExtensions.get(entry);

	if (ext instanceof MarkExtension) {
		return markActive(ext.$type)(editorState);
	} else if (ext instanceof NodeExtension) {
		return nodeActive(ext.name)(editorState);
	}
	return false;
}

export function isDisabled(entry: MenuEntry, ext: Toolbar, editorView: EditorView) {
	return !entry.run()(editorView.state, undefined, editorView);
}

export function groupEntries(entries: readonly MenuEntry[]) {
	const sections = mapValues(
		groupBy(entries, e => e.section ?? "default"),
		sec =>
			Object.entries(groupBy(sec, e => e.group ?? "default")).sort(([, as], [, bs]) => {
				const aMax = Math.max(...as.map(e => e.priority ?? 0));
				const bMax = Math.max(...bs.map(e => e.priority ?? 0));
				if (aMax > bMax) return -1;
				if (aMax < bMax) return 1;
				return 0;
			}),
	);

	Object.values(sections).forEach(groups =>
		Object.values(groups).forEach(([_name, group]) =>
			group.sort((a, b) => {
				const aP = a.priority ?? (a.group === "default" ? 1 : 0);
				const bP = b.priority ?? (b.group === "default" ? 1 : 0);
				if (aP > bP) return -1;
				if (aP < bP) return 1;
				return 0;
			}),
		),
	);

	return sections;
}

// These are essentially the breakpoints set by the design team
type ToolbarWidthState = "lte300" | "lte350" | "lte385" | "lte460" | "lte500" | "lte530" | "lte565" | "gt565";

const getToolbarWidthState = (width: number): ToolbarWidthState => {
	if (width <= 300) {
		return "lte300";
	} else if (width <= 350) {
		return "lte350";
	} else if (width <= 385) {
		return "lte385";
	} else if (width <= 460) {
		return "lte460";
	} else if (width <= 500) {
		return "lte500";
	} else if (width <= 530) {
		return "lte530";
	} else if (width <= 565) {
		return "lte565";
	} else {
		return "gt565";
	}
};

export interface ToolbarProps {
	editorView: EditorView;
	extension: Toolbar;
	noJira: boolean;
	noMention: boolean;
	isExternalUser: boolean;
	excludeChoice?: (choice: ReturnType<typeof choiceToTypeaheadResult>) => boolean;
	entries: readonly MenuEntry[];
	className?: string;
	lastUpdatedDate?: string;
	saveState?: NoteSaveStatus;
	inMostRecentlyFocusedNote: boolean;
	addBlock: (entry: MenuEntry) => void;
	onChoiceClickOverride?: (choice: TypeAheadResult, next: () => void) => void;
	transcriptModalNoteId?: string;
}

function ToolbarComponent({
	entries,
	extension,
	editorView,
	className,
	noJira,
	noMention,
	excludeChoice,
	isExternalUser,
	lastUpdatedDate,
	saveState,
	inMostRecentlyFocusedNote,
	addBlock,
	onChoiceClickOverride,
	transcriptModalNoteId,
}: ToolbarProps) {
	const { selection, doc } = editorView.state;

	let filteredEntries = entries;
	filteredEntries = entries.filter(entry => entry.group !== "insert");

	const [menuWidthState, setMenuWidthState] = React.useState<ToolbarWidthState>("gt565");
	const { ref } = useResizeObserver({
		onResize: ({ width }) => {
			if (width) {
				const menuWidth = getToolbarWidthState(width);
				setMenuWidthState(menuWidth);
			}
		},
	});

	const lte460 =
		menuWidthState === "lte460" ||
		menuWidthState === "lte385" ||
		menuWidthState === "lte350" ||
		menuWidthState === "lte300";
	const sections = groupEntries(filteredEntries);
	const currentBlockTypeEntry = flatMap(sections.types, ([, entries]) => entries).find(
		e => e.active?.(editorView.state) ?? isActive(e, extension, editorView.state),
	);

	let hasLinkedBlockInSelection = false;
	let hasImageStripInSelection =
		selection.$from.node(1)?.type.name === "image_strip" || selection.$to.node(1)?.type.name === "image_strip";
	doc.slice(selection.from, selection.to).content.descendants(node => {
		if (!hasImageStripInSelection && node.type.name.startsWith("image_strip")) {
			hasImageStripInSelection = true;
		}
		if (!hasLinkedBlockInSelection && node.type.name === "todo" && node.attrs.originalBlockId)
			hasLinkedBlockInSelection = true;
		return false;
	});

	const renderItem = React.useCallback(
		(entry: MenuEntry, isRadixMenu: boolean) => {
			return (
				<ToolbarItem
					key={entry.title}
					asMenuItem={isRadixMenu}
					isMobile={false}
					entry={entry}
					extension={extension}
					editorView={editorView}
					active={isActive(entry, extension, editorView.state)}
					disabled={(entry.greyIfDryRunFalse ?? false) && isDisabled(entry, extension, editorView)}
				/>
			);
		},
		[extension, editorView],
	);

	const renderItems = React.useCallback(
		(items: MenuEntry[], isRadixMenu: boolean) => {
			return items.map(e => renderItem(e, isRadixMenu));
		},
		[renderItem],
	);

	const renderGroup = React.useCallback(
		(items: MenuEntry[], includeSeparator = true) => {
			if (!items || items.length === 0) return;

			return (
				<>
					<div className={styles.group}>{renderItems(items, false)}</div>
					{includeSeparator && <VerticalRule />}
				</>
			);
		},
		[renderItems],
	);

	const renderMenuGroup = React.useCallback(
		(sections: MenuEntry[][], numberOfItemsToShowInBar?: number, icon?: IconDefinition, includeSeparator = true) => {
			if (!sections || sections.length === 0) return;

			const itemsToShow = sections[0].splice(0, numberOfItemsToShowInBar ?? 0);
			const groupHasActiveItemInMenu = flatten(sections).some(e => isActive(e, extension, editorView.state));

			return (
				<>
					<div className={cx(styles.group)}>
						{!!numberOfItemsToShowInBar && renderItems(itemsToShow, false)}
						<Menu>
							<Menu.Trigger
								className={cx(styles.item, {
									[styles.menuButtonActive]: groupHasActiveItemInMenu,
								})}
								asChild={false}
							>
								{icon ? (
									<>
										<FontAwesomeIcon icon={icon} fixedWidth className={styles.icon} />
										<FontAwesomeIcon icon={faCaretDown} className={styles.caret} />
									</>
								) : (
									<FontAwesomeIcon icon={faEllipsisH} fixedWidth />
								)}
							</Menu.Trigger>
							<Menu.Content>
								{sections.map((items, index) => (
									<React.Fragment key={items[0].title}>
										{index > 0 && <Menu.Separator />}
										{renderItems(items, true)}
									</React.Fragment>
								))}
							</Menu.Content>
						</Menu>
					</div>
					{includeSeparator && <VerticalRule />}
				</>
			);
		},
		[renderItems, editorView.state, extension],
	);

	const orderMenuEntriesByPriority = React.useCallback((items: MenuEntry[]) => {
		return items.sort((a, b) => {
			const aP = a.priority ?? (a.group === "default" ? 1 : 0);
			const bP = b.priority ?? (b.group === "default" ? 1 : 0);
			if (aP > bP) return -1;
			if (aP < bP) return 1;
			return 0;
		});
	}, []);

	const renderFormattingToolbarGroups = useCallback(() => {
		let mappedItems = new Map<string, MenuEntry>();

		for (let i = 0; i < filteredEntries.length; i++) {
			mappedItems.set(filteredEntries[i].title, filteredEntries[i]);
		}

		const formattingSection = compact(
			orderMenuEntriesByPriority(filteredEntries.filter(e => e.section === "formatting")),
		);
		const colouringSection = compact(
			orderMenuEntriesByPriority(filteredEntries.filter(e => e.section === "colouring")),
		);
		const indentSection = compact(orderMenuEntriesByPriority(filteredEntries.filter(e => e.section === "indent")));
		const socialSection = compact([mappedItems.get("Comment"), mappedItems.get("Reaction")]);
		const placeholderEntry = mappedItems.get("Placeholder");
		const linkEntry = mappedItems.get("Link");
		const linkSection = compact([placeholderEntry, linkEntry]);

		const proseContext = getContext(editorView.state);

		const plusButtonMenu = (
			<AddBlockMenu
				menuEntryItems={entries.filter(e => e.section === "types")}
				noJira={noJira}
				noMention={noMention}
				excludeChoice={excludeChoice}
				addBlock={addBlock}
				isExternalUser={isExternalUser}
				editorView={editorView}
				className={styles.itemToolbar}
				onChoiceClickOverride={onChoiceClickOverride}
			/>
		);

		const saveStatus = lastUpdatedDate && (
			<div className={styles.updatedIndicator}>
				{saveState && (
					<SaveState
						state={saveState}
						showOfflineTooltip={inMostRecentlyFocusedNote}
						// mostRecentlyFocusedNoteId doesn't have a value when the meeting modal is first opened
						noteId={transcriptModalNoteId ?? mostRecentlyFocusedNoteId.get()}
						hasAccessToVersioning={proseContext?.hasAccessToVersioning ?? proseContext.billing.hasAccessToVersioning}
						lastUpdatedDate={lastUpdatedDate}
					/>
				)}
			</div>
		);

		menuWidthState;

		if (menuWidthState === "lte300") {
			return (
				<>
					{renderMenuGroup([formattingSection, linkSection, colouringSection, indentSection], 3, undefined, false)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>{saveStatus}</div>
				</>
			);
		} else if (menuWidthState === "lte350") {
			return (
				<>
					{renderMenuGroup([formattingSection, linkSection, colouringSection, indentSection], 2, undefined, true)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>
						{renderGroup(socialSection, false)}
						{saveStatus}
					</div>
				</>
			);
		} else if (menuWidthState === "lte385") {
			return (
				<>
					{renderMenuGroup([formattingSection, colouringSection], 2, undefined, true)}
					{renderGroup(linkSection, false)}
					{renderMenuGroup([indentSection], 0)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>
						{renderGroup(socialSection, false)}
						{saveStatus}
					</div>
				</>
			);
		} else if (menuWidthState === "lte460" || menuWidthState === "lte500") {
			return (
				<>
					{renderMenuGroup([formattingSection, colouringSection], 2, undefined, true)}
					{renderGroup(linkSection, false)}
					{renderMenuGroup([indentSection], 0)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>
						{renderGroup(socialSection, false)}
						{saveStatus}
					</div>
				</>
			);
		} else if (menuWidthState === "lte530") {
			return (
				<>
					{renderMenuGroup([formattingSection], 2)}
					{renderGroup(linkSection, false)}
					{renderMenuGroup([indentSection], 0)}
					{renderGroup(colouringSection)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>
						{renderGroup(socialSection, false)}
						{saveStatus}
					</div>
				</>
			);
		} else if (menuWidthState === "lte565") {
			return (
				<>
					{renderMenuGroup([formattingSection], 3)}
					{renderGroup(linkSection, false)}
					{renderMenuGroup([indentSection], 0)}
					{renderGroup(colouringSection)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>
						{renderGroup(socialSection, false)}
						{saveStatus}
					</div>
				</>
			);
		} else {
			return (
				<>
					{renderMenuGroup([formattingSection], 3)}
					{renderGroup(linkSection)}
					{renderGroup(colouringSection)}
					{renderGroup(indentSection)}
					{plusButtonMenu}
					<div className={styles.menuRightAlign}>
						{renderGroup(socialSection, false)}
						{saveStatus}
					</div>
				</>
			);
		}
	}, [
		orderMenuEntriesByPriority,
		filteredEntries,
		editorView,
		entries,
		noJira,
		noMention,
		excludeChoice,
		addBlock,
		isExternalUser,
		onChoiceClickOverride,
		lastUpdatedDate,
		saveState,
		inMostRecentlyFocusedNote,
		transcriptModalNoteId,
		menuWidthState,
		renderMenuGroup,
		renderGroup,
	]);

	const formattingToolbarGroups = renderFormattingToolbarGroups();

	return (
		<div ref={ref} className={cx(styles.menu, className)}>
			{!hasLinkedBlockInSelection && sections.types.reduce((n, [, { length }]) => n + length, 0) > 1 && (
				<>
					<div className={styles.group}>
						<Menu>
							<Menu.Trigger asChild={false} className={cx(styles.item, styles.blockSelectorItem)}>
								{currentBlockTypeEntry ? (
									<>
										{currentBlockTypeEntry.icon && (
											<>
												<FontAwesomeIcon icon={currentBlockTypeEntry.icon} fixedWidth className={styles.icon} />{" "}
												{!lte460 && <div className={styles.selectedTitle}>{currentBlockTypeEntry.title}</div>}
											</>
										)}
									</>
								) : (
									<>
										<FontAwesomeIcon icon={faLayerGroup} fixedWidth className={styles.icon} />
										{!lte460 && <div className={styles.selectedTitle}>Mixed</div>}
									</>
								)}
								<FontAwesomeIcon icon={faCaretDown} className={styles.caret} />
							</Menu.Trigger>
							<Menu.Content>
								{sections.types.map(([name, entries]) => (
									<React.Fragment key={name}>
										{entries.map(e => (
											<ToolbarItem
												key={e.title}
												asMenuItem
												isMobile={false}
												entry={e}
												extension={extension}
												editorView={editorView}
												active={isActive(e, extension, editorView.state)}
												disabled={(e.greyIfDryRunFalse ?? false) && isDisabled(e, extension, editorView)}
											/>
										))}
									</React.Fragment>
								))}
							</Menu.Content>
						</Menu>
					</div>
					<VerticalRule />
				</>
			)}
			{formattingToolbarGroups}
		</div>
	);
}

const mostRecentlyFocusedNoteId = new (class MostRecentlyFocusedNoteId extends EventTarget<
	{
		"note focused": CustomEvent<{ id: string }>;
	},
	{},
	"strict"
> {
	private id?: string;

	get() {
		return this.id;
	}

	set(id: string) {
		this.id = id;
		this.dispatchEvent(new CustomEvent("note focused", { detail: { id } }));
	}
})();

export function VerticalRule() {
	return <span className={styles.verticalRule} />;
}

export function OfflineIndicator({ showOfflineTooltip }: { showOfflineTooltip: boolean }) {
	const [showTooltip, setShowTooltip] = React.useState(false);

	React.useEffect(() => {
		const timeout = setTimeout(() => {
			setShowTooltip(true);
		}, 5000);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Tooltip
			animation={false}
			visible={showOfflineTooltip && showTooltip}
			content={<>We&rsquo;re having connectivity issues</>}
		>
			<span className={cx(styles.stateOffline, "fa-layers fa-fw")}>
				<FontAwesomeIcon icon={faBolt} fixedWidth />
				<FontAwesomeIcon icon={faSlash} fixedWidth />
				<FontAwesomeIcon icon={faSlash} fixedWidth transform="left-1 down-1" />
			</span>
		</Tooltip>
	);
}

export function SaveState({
	state,
	showOfflineTooltip,
	hasAccessToVersioning,
	noteId,
	lastUpdatedDate,
}: {
	state: "offline" | "saving" | string;
	showOfflineTooltip: boolean;
	hasAccessToVersioning: boolean;
	noteId?: string;
	lastUpdatedDate?: string;
}) {
	const onHistoryClickCallback = useCallback(() => {
		if (noteId) openNoteHistoryModal(noteId, hasAccessToVersioning);
	}, [hasAccessToVersioning, noteId]);

	if (state === "offline") {
		return <OfflineIndicator showOfflineTooltip={showOfflineTooltip} />;
	} else if (state === "saving") {
		return <FontAwesomeIcon icon={faEllipsisH} fixedWidth className={styles.stateSaving} />;
	} else {
		return (
			<Tooltip content={`Updated ${lastUpdatedDate}`}>
				{hasAccessToVersioning ? (
					<ButtonLink onClick={onHistoryClickCallback} className={styles.historyButton}>
						<FontAwesomeIcon icon={faCheck} fixedWidth className={styles.stateSaved} data-test-id="note-saved-check" />
					</ButtonLink>
				) : (
					<FontAwesomeIcon icon={faCheck} fixedWidth className={styles.stateSaved} data-test-id="note-saved-check" />
				)}
			</Tooltip>
		);
	}
}

type NoteSaveStatus = "saved" | "saving" | "offline";

function plusChoices(
	view: EditorView,
	{
		noJira,
		noMention,
		isExternalUser,
		skipFirstToolbarUndefined,
		excludeChoice,
		isMediumScreen,
		isExtraSmallScreen,
	}: {
		noJira?: boolean;
		noMention?: boolean;
		isExternalUser?: boolean;
		skipFirstToolbarUndefined: number;
		excludeChoice?: (choice: ReturnType<typeof choiceToTypeaheadResult>) => boolean;
		isMediumScreen?: boolean;
		isExtraSmallScreen?: boolean;
	} = { skipFirstToolbarUndefined: 1000 },
) {
	const choices: ReturnType<typeof getMenuChoices> = [];
	const featureControlledChoices: ReturnType<typeof getMenuChoices> = [];
	let remainingToolbarUndefineToSkip = skipFirstToolbarUndefined;

	for (const c of getMenuChoices(isExternalUser ?? false)) {
		if (c.showInToolbar === undefined && remainingToolbarUndefineToSkip-- > 0) continue;
		if (c.showInToolbar === false) continue;
		if (noJira && c.title === "Jira") continue;
		if (noMention && c.title === "Mention") continue;
		if (excludeChoice && excludeChoice(c)) continue;
		if (!isExtraSmallScreen && c.showOnlyOnExtraSmallScreen) continue;
		if (!isMediumScreen && c.showOnlyOnMediumScreen) continue;

		(c.withFeature ? featureControlledChoices : choices).push({
			...c,
			command: (editorState: EditorState) => {
				const inner = c.command(editorState);
				if (!inner) return inner;
				return (state: EditorState, dispatch: (tr: Transaction) => void) => {
					view.focus();
					return inner(state, dispatch, view);
				};
			},
		});
	}

	return [choices, featureControlledChoices];
}

export interface AddBlockMenuProps {
	className?: string;

	/** Add a new block to the note on the line beneath the user's cursor.  */
	editorView: EditorView;
	menuEntryItems?: MenuEntry[];

	noJira: boolean;
	noMention: boolean;
	isExternalUser: boolean;
	excludeChoice?: (choice: ReturnType<typeof choiceToTypeaheadResult>) => boolean;
	compact?: boolean;
	addBlock: (entry: MenuEntry) => void;
	onChoiceClickOverride?: (choice: TypeAheadResult, next: () => void) => void;
}

function AddBlockMenu({
	editorView,
	className,
	noJira,
	noMention,
	isExternalUser,
	excludeChoice,
	compact,
	menuEntryItems,
	addBlock: _addBlock,
	onChoiceClickOverride,
}: AddBlockMenuProps) {
	const toolbarRef = React.useRef<HTMLDivElement>(null);
	const { isMobile: isMediumScreen, isExtraSmallScreen } = React.useContext(ViewportContext);

	const addBlock = React.useMemo(() => memoize((entry: MenuEntry) => () => _addBlock(entry)), [_addBlock]);
	const isRecording = useRecordingState();

	let skipFirstToolbarUndefined = 100; // Note: 100 means just a "big" number (greater than plus choices)

	const [choices, featureControlledChoices] = React.useMemo(
		() =>
			plusChoices(editorView, {
				noJira,
				noMention,
				isExternalUser,
				skipFirstToolbarUndefined,
				excludeChoice,
				isMediumScreen: isMediumScreen || compact,
				isExtraSmallScreen: isExtraSmallScreen || compact,
			}),
		[
			editorView,
			noJira,
			noMention,
			isExternalUser,
			skipFirstToolbarUndefined,
			excludeChoice,
			isMediumScreen,
			isExtraSmallScreen,
			compact,
		],
	);

	// Allow for some menu choices to be hidden according to feature flags
	const [plusMenuChoices, setPlusMenuChoices] = React.useState(choices);
	React.useEffect(() => {
		setPlusMenuChoices(choices);
		featureControlledChoices.forEach(async choice => {
			if (!choice.withFeature) {
				setPlusMenuChoices(prev => prev.filter(c => c.title === choice.title));
				return;
			}

			if (await fetchFeature(choice.withFeature.id, choice.withFeature.subject)) {
				setPlusMenuChoices(prev => [...prev, choice]);
			}
		});
	}, [choices, featureControlledChoices]);

	const onChoiceClick = React.useMemo(
		() =>
			memoize((result: TypeAheadResult) => () => {
				const cb = () =>
					result.command(editorView.state)?.(
						editorView.state,
						tr => editorView.dispatch(tr.scrollIntoView()),
						editorView,
					);
				if (onChoiceClickOverride) {
					return onChoiceClickOverride(result, cb);
				}
				return cb();
			}),
		[editorView, onChoiceClickOverride],
	);

	const [isOpen, setIsOpen] = React.useState(false);
	const toggleOpen = useCallback((open: boolean) => setIsOpen(open), []);

	const isIconDef = (item: SlashCommandChoice["icon"]): item is IconDefinition => {
		return Boolean(item && typeof item == "object" && "icon" in item);
	};

	return (
		<div ref={toolbarRef} className={cx(styles.itemToolbar, className)}>
			<Menu onOpenChange={toggleOpen}>
				<Tooltip content="Insert…">
					<Menu.Trigger asChild={false} className={styles.plusButton}>
						<span className={styles.plusIconContainer}>
							<FontAwesomeIcon fixedWidth icon={faPlus} />
						</span>
						<FontAwesomeIcon className={styles.plusIconCaret} icon={isOpen ? faCaretUp : faCaretDown} />
					</Menu.Trigger>
				</Tooltip>
				<Menu.Content
					// If we're inside a modal, there'll be a scroll lock, and it won't be able to tell
					// that the menu content is "inside" the modal because it uses a separate React tree.
					// So we'll portal inside the modal if there is one. Otherwise we portal to the body
					// like usual.
					portalContainer={editorView.dom.closest<HTMLElement>("[role=dialog]") ?? undefined}
					data-ignore-focus-lock
					side="bottom"
					align="start"
					className={styles.plusMenu}
				>
					{menuEntryItems &&
						menuEntryItems.map(e => (
							<Menu.Item key={e.title} onSelect={addBlock(e)} disabled={isRecording && e.title === "Record video"}>
								<div className={styles.plusMenuItem}>
									{e.icon && (
										<div className={styles.plusMenuItemIcon}>
											<FontAwesomeIcon icon={e.icon} />
										</div>
									)}
									<div className={styles.plusMenuItemText}>
										<div className={styles.plusMenuItemTitle}>{e.title}</div>
										<p>{e.subtitle}</p>
									</div>
									<div className={styles.plusMenuShortcut}>{e?.keyboardShortcut}</div>
								</div>
							</Menu.Item>
						))}
					{plusMenuChoices.map(c => (
						<Menu.Item key={c.title} onSelect={onChoiceClick(c)}>
							<div className={styles.plusMenuItem}>
								<div className={styles.plusMenuItemIcon}>
									{!isIconDef(c.icon) ? (
										<span className={styles.plusMenuTextIcon}>{c.icon}</span>
									) : (
										<FontAwesomeIcon icon={c.icon} />
									)}
								</div>
								<div className={styles.plusMenuItemText}>
									<div className={styles.plusMenuItemTitle}>{c.title}</div>
									<p>{c.content}</p>
								</div>
								<div className={styles.plusMenuShortcut}>{c.shortcut}</div>
							</div>
						</Menu.Item>
					))}
				</Menu.Content>
			</Menu>
		</div>
	);
}

export class Toolbar extends MenuExtension {
	mobileContainer: HTMLElement;
	pendingUpdate: boolean;
	toolbarMountPoint?: HTMLElement;
	attachmentsContainer?: HTMLElement;
	hasFocusedNote: boolean;

	get name() {
		return "menu";
	}

	constructor(
		options: {
			setAttachmentsRef?: (el: HTMLElement) => void;
			noJira?: boolean;
			noMention?: boolean;
			isExternalUser?: boolean;
			excludeChoice?: (choice: ReturnType<typeof choiceToTypeaheadResult>) => boolean;
			compact?: boolean;
			mountPointId?: string;
			className?: string;
			postProcessEntries?: (entries: MenuEntry[]) => MenuEntry[];
			onChoiceClickOverride?: (choice: TypeAheadResult, next: () => void) => void;
		} = {},
	) {
		super(options);
		this.mobileContainer = document.createElement("div");
		this.mobileContainer.classList.add(this.options.containerClass);
		this.pendingUpdate = false;
		this.hasFocusedNote = false;

		if (options.setAttachmentsRef) {
			this.attachmentsContainer = document.createElement("div");
		}
	}

	get defaultOptions() {
		return {
			containerClass: "prosemirror-menu",
			menuMobileClass: "prosemirror-menu-mobile",
		};
	}

	get plugins() {
		const extension = this;
		return [
			new Plugin({
				view: () => {
					return {
						update: extension.update,
					};
				},

				appendTransaction(trs, _oldState, newState) {
					if (extension.isMobile && trs.some(tr => (tr.docChanged || tr.selectionSet) && !tr.getMeta("remoteChange"))) {
						return newState.tr.scrollIntoView();
					}

					return undefined;
				},

				props: {
					handleDOMEvents: {
						focus(view) {
							const context = getContext(view.state);

							extension.hasFocusedNote = true;

							if (context && typeof context === "object" && "noteId" in context) {
								mostRecentlyFocusedNoteId.set(context.noteId);
							}
							return false;
						},
					},
				},
			}),
		];
	}

	get isMobile() {
		return window.matchMedia("(max-width: 768px)").matches && !window.matchMedia("(hover: hover)").matches;
	}

	init() {
		if (!this.editorView || !this.editorView.dom.parentNode) return;

		document.addEventListener("selectionchange", this.onSelectionChange);

		CollabPluginKey.get(this.editorView.state)
			?.getState(this.editorView.state)
			?.addEventListener("statuschange", this.getViewAndUpdate);

		this.editorView.dom.addEventListener("focus", this.onDOMFocus);

		if (window.visualViewport) {
			window.visualViewport.addEventListener("resize", this.adjust);
			window.visualViewport.addEventListener("scroll", this.adjust);
		}

		if (!this.editorView || !this.editorView.dom.parentNode) return;

		mostRecentlyFocusedNoteId.addEventListener("note focused", this.getViewAndUpdate);

		const context = getContext(this.editorView.state);
		if (context?.isFirstNote) mostRecentlyFocusedNoteId.set(context.noteId);

		if (this.attachmentsContainer && this.options.setAttachmentsRef) {
			this.editorView.dom.parentNode.appendChild(this.attachmentsContainer);
			this.options.setAttachmentsRef(this.attachmentsContainer);
		}

		const mountPoint = document.getElementById(this.options.mountPointId);
		this.toolbarMountPoint = mountPoint ?? undefined;

		if (this.isMobile) return;
		if (!mountPoint) return;

		this.update(this.editorView);
	}

	destroy = () => {
		this.unmountMobileToolbar();
		this.mobileContainer.remove();

		if (window.visualViewport) {
			window.visualViewport.removeEventListener("resize", this.adjust);
			window.visualViewport.removeEventListener("scroll", this.adjust);
		}

		if (this.editorView) {
			this.editorView.dom?.removeEventListener("focus", this.onDOMFocus);

			CollabPluginKey.get(this.editorView.state)
				?.getState(this.editorView.state)
				?.removeEventListener("statuschange", this.getViewAndUpdate);
		}

		if (this.toolbarMountPoint) {
			ReactDOM.unmountComponentAtNode(this.toolbarMountPoint);
		}

		mostRecentlyFocusedNoteId.removeEventListener("note focused", this.getViewAndUpdate);

		document.removeEventListener("selectionchange", this.onSelectionChange);
	};

	onDOMFocus = () => {
		if (this.isMobile) {
			this.showMobileToolbar();
		}
	};

	onSelectionChange = () => {
		if (!this.editorView?.hasFocus()) this.hideMobileToolbar();
	};

	private addBlock = (menuEntry: MenuEntry) => {
		if (!this.editorView) return;
		const hasFocusedNote = this.hasFocusedNote;
		this.editorView.focus();
		const { state, dispatch } = this.editorView;

		if (menuEntry.group === "insert") {
			menuEntry.run()(state, tr => (dispatch(tr.scrollIntoView()), true), this.editorView);
			return;
		}

		const { $from } = state.selection;
		const { indent } = $from.node().attrs;
		const attrs: { blockId: string; indent?: number } = { blockId: uuid4() };
		if (indent !== undefined) attrs.indent = indent;
		const newBlock = state.schema.nodes.unstyled.createAndFill(attrs);
		if (!newBlock) {
			console.error("Failed to create a new block");
			return;
		}

		let { tr } = state;

		let selectedBlock = $from.node(1) as ProseNode | undefined | null;
		if (state.selection instanceof NodeSelection) {
			selectedBlock = state.selection.$from.nodeAfter;
		}
		let pos = 0;

		if (hasFocusedNote) {
			for (let i = 0; i < tr.doc.childCount; i += 1) {
				const child = tr.doc.child(i);
				pos += child.nodeSize;
				if (selectedBlock && child.attrs.blockId === selectedBlock.attrs.blockId) break;
			}
		} else {
			pos = 0;
		}

		tr = tr.insert(pos, newBlock);

		tr = tr.setSelection(new TextSelection(tr.doc.resolve(pos + 1)));
		dispatch(tr);

		menuEntry.run()(this.editorView.state, tr => (dispatch(tr.scrollIntoView()), true), this.editorView);
	};

	private saveState(connectionState?: { saved: boolean; online: boolean }): NoteSaveStatus | undefined {
		if (!this.editorView) return "saving";
		if (!connectionState) {
			const collabPlugin = CollabPluginKey?.get(this.editorView.state);
			if (!collabPlugin) return undefined;
			const collabState = collabPlugin.getState(this.editorView.state);
			// eslint-disable-next-line no-param-reassign
			connectionState = collabState?.connectionState;
		}

		return connectionState?.online ? (connectionState.saved ? "saved" : "saving") : "offline";
	}

	private didSetScrollThresholdAndMargin?: boolean;
	private setScrollThresholdAndMargin = () => {
		if (this.didSetScrollThresholdAndMargin) return;
		if (!this.editorView) return;
		this.didSetScrollThresholdAndMargin = true;

		const { editorView } = this;

		fastdom.measure(() => {
			const toolbarHeight = (this.isMobile ? this.mobileContainer : undefined)?.getBoundingClientRect().height ?? 0;
			// this assumes this variable is set in px, which, as of 2020-12-18, it is
			const headerHeight = parseInt(
				window.getComputedStyle(editorView.dom).getPropertyValue("--stream-header-height") || "0",
				10,
			);
			const otherSides = { left: 0, top: headerHeight, right: 0 };
			fastdom.mutate(() => {
				// @ts-ignore
				if (editorView.docView) {
					editorView.update({
						...editorView.props,
						scrollMargin: { bottom: toolbarHeight + 5, ...otherSides },
						scrollThreshold: { bottom: toolbarHeight, ...otherSides },
					});
				}
			});
		});
	};

	adjust = () => {
		/* Safari on iOS (and only safari, WKWebView alone works fine) does not work
		 * with fixed elements and scrolling. This works around that in iOS 13
		 * position after resize and scroll events. All other browsers should
		 * when using this. */
		if (this.pendingUpdate) return;
		this.pendingUpdate = true;
		requestAnimationFrame(() => {
			this.pendingUpdate = false;

			const viewport = window.visualViewport;
			if (!viewport) return;

			const { mobileContainer } = this;
			const offsetX = viewport.offsetLeft;
			const offsetY = viewport.height - window.document.body.getBoundingClientRect().height + viewport.offsetTop;
			const scale = 1 / viewport.scale;
			mobileContainer.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) scale(${scale})`;
		});
	};

	unmountMobileToolbar = () => {
		ReactDOM.unmountComponentAtNode(this.mobileContainer);
	};

	getViewAndUpdate = () => {
		if (!this.editorView) return;
		this.update(this.editorView);
	};

	updateMobileMenu = (view: EditorView) => {
		const canShowMobileMenu = view.editable && view.hasFocus() && this.isMobile;

		if (canShowMobileMenu) {
			this.showMobileToolbar();
		} else {
			this.hideMobileToolbar();
		}
	};

	update = (view: EditorView) => {
		this.updateMobileMenu(view);

		if (this.toolbarMountPoint) {
			if (this.isMobile) {
				ReactDOM.unmountComponentAtNode(this.toolbarMountPoint);
				return;
			}

			const context = getContext(view.state);
			const inMostRecentlyFocusedNote = mostRecentlyFocusedNoteId.get() === context?.noteId;

			ReactDOM.render(
				<ToolbarComponent
					noJira={!!this.options.noJira}
					noMention={!!this.options.noMention}
					excludeChoice={this.options.excludeChoice}
					lastUpdatedDate={context?.getLastUpdatedString?.()}
					saveState={this.saveState()}
					inMostRecentlyFocusedNote={inMostRecentlyFocusedNote}
					isExternalUser={!!this.options.isExternalUser}
					entries={this.allEntries}
					editorView={view}
					extension={this}
					addBlock={this.addBlock}
					className={this.options.className}
					onChoiceClickOverride={this.options.onChoiceClickOverride}
					transcriptModalNoteId={context?.transcriptModalNoteId}
				/>,
				this.toolbarMountPoint,
			);
		}
	};

	closeKeyboard = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		this.hideMobileToolbar();
	};

	static getElementClientRect(element: HTMLElement, displayType = "block") {
		const copy = element.cloneNode(true) as HTMLElement;
		document.body.appendChild(copy);
		copy.style.left = "-10000px";
		copy.style.display = displayType;
		const rect = copy.getBoundingClientRect();
		copy.remove();
		return rect;
	}

	undo = () => {
		if (!this.editorView) return;
		this.editorView.focus();
		const { state, dispatch } = this.editorView;
		undo(state, dispatch, this.editorView);
	};
	showMobileToolbar = () => {
		if (!this.editorView || !this.editorView.dom.parentNode) return;
		if (this.isMobile) {
			if (!this.mobileContainer.parentNode) {
				document.body.appendChild(this.mobileContainer);
			}
			this.mobileContainer.classList.add(this.options.menuMobileClass);
			const context = getContext(this.editorView.state);
			ReactDOM.render(
				<MobileToolbar
					extension={this}
					editorView={this.editorView}
					closeKeyboard={this.closeKeyboard}
					saveState={this.saveState()}
					entries={this.allEntries}
					addBlock={this.addBlock}
					onMeetingFeedbackClick={context?.onMeetingFeedbackClick}
					meetingFeedbackSent={context?.meetingFeedbackSent ?? false}
					meetingFeedbackResponseCount={context?.meetingFeedbackResponseCount ?? 0}
					lastUpdatedDate={context?.getLastUpdatedString?.()}
					undo={this.undo}
				/>,
				this.mobileContainer,
				this.setScrollThresholdAndMargin,
			);
		}
	};

	hideMobileToolbar() {
		this.mobileContainer.classList.remove(this.options.menuMobileClass);
		this.mobileContainer.style.bottom = "";
		this.mobileContainer.style.left = "";
		this.unmountMobileToolbar();
		if (this.mobileContainer.parentNode) {
			this.mobileContainer.remove();
		}
	}
}

export default Toolbar;
